
import { Book } from "@/types/book";
import { EnhancedBookCover } from "./ui/enhanced-book-cover";
import { MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { useMostReviewedBooks } from "@/hooks/use-most-reviewed-books";
import { NetflixBookSkeleton } from "./skeletons/NetflixBookSkeleton";
import { useCallback, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { BookHoverPreview } from "./BookHoverPreview";
import { useIsMobile } from "@/hooks/use-mobile";
import { THUMBNAIL_SIZE, SECTION_TITLE } from "@/lib/layout-constants";
import { useBookCarousel } from "@/hooks/use-book-carousel";

interface MostReviewedSectionProps {
  onBookClick: (book: Book) => void;
  allBooks: Book[];
}

export function MostReviewedSection({ onBookClick, allBooks }: MostReviewedSectionProps) {
  const { data: books, isLoading } = useMostReviewedBooks(allBooks, 9);
  const isMobile = useIsMobile();
  const { emblaRef, scrollPrev, scrollNext, isDragging } = useBookCarousel({
    loop: false,
    dragFree: true,
  });
  
  // Hover preview state
  const [hoveredBook, setHoveredBook] = useState<Book | null>(null);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = useCallback((book: Book, element: HTMLDivElement) => {
    if (isMobile) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setAnchorRect(element.getBoundingClientRect());
      setHoveredBook(book);
    }, 1000);
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const closePreview = useCallback(() => {
    setHoveredBook(null);
    setAnchorRect(null);
  }, []);

  if (isLoading) {
    return (
      <div className="relative overflow-x-hidden">
        <div className="px-5 md:px-12 xl:px-16 mb-2 md:mb-3">
          <div className="h-8 w-48 rounded bg-white/5 overflow-hidden relative">
            <div 
              className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite]"
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }}
            />
          </div>
        </div>
        <div className="overflow-hidden pl-5 md:pl-12 xl:pl-16 pr-5 md:pr-12 xl:pr-16">
          <div className="flex gap-3 pb-4 md:pb-6">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i} 
                className={`flex-shrink-0 ${THUMBNAIL_SIZE.full}`}
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <NetflixBookSkeleton />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!books || books.length === 0) {
    return null;
  }

  return (
    <div className="relative overflow-x-hidden">
      {/* Title with Navigation */}
      <div className="px-5 md:px-12 xl:px-16 mb-2 md:mb-3 flex items-center justify-between">
        <h2 className={SECTION_TITLE.full}>
          Most Reviewed
        </h2>
        <div className="hidden md:flex gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={scrollPrev}
            className="h-10 w-10 rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={scrollNext}
            className="h-10 w-10 rounded-full border-white/20 bg-white/5 hover:bg-white/10 text-white"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
      
      {/* Embla carousel */}
      <div 
        ref={emblaRef} 
        className="overflow-hidden embla-carousel"
        data-dragging={isDragging}
      >
        <div className="flex gap-3 pt-2 pb-4 md:pb-6 pl-5 md:pl-12 xl:pl-16 embla__container">
          {books.map((book, index) => {
            const ranking = index + 1;
            
            return (
              <div
                key={book.id}
                className={`relative flex-none group embla__slide ${index === 0 ? 'pl-2' : ''}`}
                onMouseEnter={(e) => handleMouseEnter(book, e.currentTarget as HTMLDivElement)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Book Card with Ranking Number Overlay */}
                <div
                  onClick={() => { closePreview(); onBookClick(book); }}
                  className="relative z-10 cursor-pointer"
                >
                  <div className={`relative ${THUMBNAIL_SIZE.full}`}>
                    {/* Ranking Number - behind the book cover */}
                    <div className="absolute -left-2 top-0 bottom-0 z-0 pointer-events-none select-none flex items-center">
                      <span
                        className="text-[60px] sm:text-[70px] lg:text-[90px] xl:text-[100px] font-black leading-none"
                        style={{
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.5) 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          textShadow: '0 0 40px rgba(168, 85, 247, 0.3)',
                          filter: 'drop-shadow(0 4px 20px rgba(168, 85, 247, 0.2))',
                        }}
                      >
                        {ranking}
                      </span>
                    </div>
                    
                    <div className="relative z-10 aspect-[2/3] rounded-[8px] border-2 border-transparent lg:group-hover:border-white/40 transition-all duration-300 overflow-hidden shadow-2xl ml-4 sm:ml-5 lg:ml-6 xl:ml-7">
                      <EnhancedBookCover
                        src={book.cover_image_url || ''}
                        alt={book.title}
                        bookId={book.id}
                        cacheVersion={book.cache_version}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Review Count Badge */}
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20 ml-2 sm:ml-2.5 lg:ml-3 xl:ml-3.5">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 shadow-lg">
                        <MessageSquare className="w-3.5 h-3.5 text-white" />
                        <span className="text-xs font-bold text-white">
                          {(book.review_count || 0) >= 1000 
                            ? `${((book.review_count || 0) / 1000).toFixed(1)}k` 
                            : book.review_count || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Hover Preview */}
      {hoveredBook && anchorRect && (
        <BookHoverPreview
          book={hoveredBook}
          anchorRect={anchorRect}
          onClose={closePreview}
          onOpenDialog={() => { closePreview(); onBookClick(hoveredBook); }}
          onPlay={() => { closePreview(); onBookClick(hoveredBook); }}
        />
      )}
    </div>
  );
}
